export type KnowledgeBaseUploadDestination =
  | { kind: "root" }
  | { kind: "existing-folder"; folderId: string }
  | { kind: "new-folder"; folderName: string }

export type KnowledgeBaseUploadGroup = {
  destination: KnowledgeBaseUploadDestination
  files: File[]
}

export type KnowledgeBaseUploadLimits = {
  maxFiles: number
  maxBytes: number | null
}

export const UNLIMITED_KNOWLEDGE_BASE_BATCH_FILE_CAP = 100
const BYTES_PER_MEBIBYTE = 1024 * 1024

export function resolveKnowledgeBaseUploadLimits(input: {
  documentLimit: number | null | undefined
  currentDocumentCount: number | null | undefined
  sizeLimitMb: number | null | undefined
  currentSizeMb: number | null | undefined
}): KnowledgeBaseUploadLimits {
  const currentDocumentCount = Math.max(0, input.currentDocumentCount ?? 0)
  let maxFiles = UNLIMITED_KNOWLEDGE_BASE_BATCH_FILE_CAP
  if (typeof input.documentLimit === "number") {
    maxFiles = Math.max(0, input.documentLimit - currentDocumentCount)
  }

  if (typeof input.sizeLimitMb !== "number") {
    return { maxFiles, maxBytes: null }
  }

  const remainingSizeMb = Math.max(0, input.sizeLimitMb - (input.currentSizeMb ?? 0))
  return {
    maxFiles,
    maxBytes: Math.floor(remainingSizeMb * BYTES_PER_MEBIBYTE),
  }
}

export function validateKnowledgeBaseUploadSelection(
  files: readonly File[],
  limits: KnowledgeBaseUploadLimits,
): string | null {
  if (files.length > limits.maxFiles) {
    return `You can upload up to ${limits.maxFiles} more document${limits.maxFiles === 1 ? "" : "s"} on the current plan.`
  }

  const selectedBytes = files.reduce((total, file) => total + file.size, 0)
  if (limits.maxBytes !== null && selectedBytes > limits.maxBytes) {
    return `Selected files total ${formatFileSize(selectedBytes)}, but only ${formatFileSize(limits.maxBytes)} of Knowledge Base storage remains.`
  }

  return null
}

type FileWithRelativePath = File & {
  path?: string
}

export function planKnowledgeBaseUploads(
  files: readonly File[],
  currentFolderId: string | null,
): KnowledgeBaseUploadGroup[] {
  if (files.length === 0) return []

  if (currentFolderId) {
    return [{
      destination: { kind: "existing-folder", folderId: currentFolderId },
      files: [...files],
    }]
  }

  const rootFiles: File[] = []
  const folderFiles = new Map<string, File[]>()

  for (const file of files) {
    const folderName = getTopLevelFolderName(file)
    if (!folderName) {
      rootFiles.push(file)
      continue
    }

    const groupedFiles = folderFiles.get(folderName) ?? []
    groupedFiles.push(file)
    folderFiles.set(folderName, groupedFiles)
  }

  const groups: KnowledgeBaseUploadGroup[] = []
  if (rootFiles.length > 0) {
    groups.push({ destination: { kind: "root" }, files: rootFiles })
  }
  for (const [folderName, groupedFiles] of folderFiles) {
    groups.push({
      destination: { kind: "new-folder", folderName },
      files: groupedFiles,
    })
  }

  return groups
}

export function getKnowledgeBaseUploadPath(file: File): string {
  const fileWithPath = file as FileWithRelativePath
  const candidate = file.webkitRelativePath || fileWithPath.path || ""
  return candidate.replace(/\\/g, "/").replace(/^\.\//, "").replace(/^\/+/, "")
}

function getTopLevelFolderName(file: File): string | null {
  const pathParts = getKnowledgeBaseUploadPath(file).split("/").filter(Boolean)
  return pathParts.length > 1 ? pathParts[0] : null
}

function formatFileSize(bytes: number): string {
  if (bytes < BYTES_PER_MEBIBYTE) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / BYTES_PER_MEBIBYTE).toFixed(1)} MB`
}
